import React, { useRef, useState } from 'react';
import {
  useListWhatsNewQuery,
  useCreateWhatsNewMutation,
  useUpdateWhatsNewMutation,
  useDeleteWhatsNewMutation,
  useUploadWhatsNewImageMutation,
  type WhatsNewBlock,
  type WhatsNewEntry,
  type WhatsNewLocale,
  type WhatsNewTranslation,
  type WhatsNewTranslations,
} from '../../redux/whatsNew/whatsNew.api';

type EditLocale = 'en' | WhatsNewLocale;

const LOCALES: Array<{ code: EditLocale; label: string }> = [
  { code: 'en', label: '🇬🇧 English' },
  { code: 'ua', label: '🇺🇦 Українська' },
  { code: 'ru', label: '🌐 Русский' },
  { code: 'sk', label: '🇸🇰 Slovenčina' },
  { code: 'de', label: '🇩🇪 Deutsch' },
];

const TRANSLATED_LOCALES: WhatsNewLocale[] = ['ua', 'ru', 'sk', 'de'];

interface FormState {
  title: string;
  iosVersion: string;
  androidVersion: string;
  isPublished: boolean;
  blocks: WhatsNewBlock[];
  translations: WhatsNewTranslations;
}

const emptyForm: FormState = {
  title: '',
  iosVersion: '',
  androidVersion: '',
  isPublished: false,
  blocks: [],
  translations: {},
};

const emptyTranslation: WhatsNewTranslation = { title: '', blocks: [] };

const formFromEntry = (entry: WhatsNewEntry): FormState => ({
  title: entry.title,
  iosVersion: entry.iosVersion,
  androidVersion: entry.androidVersion,
  isPublished: entry.isPublished,
  blocks: entry.blocks,
  translations: entry.translations ?? {},
});

const extractErrorMessage = (err: unknown): string => {
  if (typeof err === 'object' && err !== null) {
    const e = err as { data?: { message?: string }; status?: number | string };
    if (e.data?.message) return e.data.message;
    if (e.status === 'FETCH_ERROR') return 'Network error.';
  }
  return 'Save failed';
};

export const AdminWhatsNew = (): React.JSX.Element => {
  const { data, isLoading, error } = useListWhatsNewQuery();
  const [createEntry, { isLoading: isCreating }] = useCreateWhatsNewMutation();
  const [updateEntry, { isLoading: isUpdating }] = useUpdateWhatsNewMutation();
  const [deleteEntry] = useDeleteWhatsNewMutation();
  const [uploadImage, { isLoading: isUploading }] =
    useUploadWhatsNewImageMutation();

  // null = list view, 'new' = creating, otherwise id of the entry being edited
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [activeLang, setActiveLang] = useState<EditLocale>('en');
  const [saveError, setSaveError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Index of the image block a picked file should land in; null appends a new block.
  const uploadTargetRef = useRef<number | null>(null);

  const isSaving = isCreating || isUpdating;

  const openNew = (): void => {
    setForm(emptyForm);
    setActiveLang('en');
    setSaveError(null);
    setEditingId('new');
  };

  const openEntry = (entry: WhatsNewEntry): void => {
    setForm(formFromEntry(entry));
    setActiveLang('en');
    setSaveError(null);
    setEditingId(entry.id);
  };

  const closeEditor = (): void => {
    setEditingId(null);
    setSaveError(null);
  };

  // ─── Active-language content ─────────────────────────────

  const activeContent: WhatsNewTranslation =
    activeLang === 'en'
      ? { title: form.title, blocks: form.blocks }
      : (form.translations[activeLang] ?? emptyTranslation);

  const setActiveContent = (patch: Partial<WhatsNewTranslation>): void => {
    setForm((prev) => {
      if (activeLang === 'en') {
        return { ...prev, ...patch };
      }
      const current = prev.translations[activeLang] ?? emptyTranslation;
      return {
        ...prev,
        translations: {
          ...prev.translations,
          [activeLang]: { ...current, ...patch },
        },
      };
    });
  };

  const setBlocks = (
    mutate: (blocks: WhatsNewBlock[]) => WhatsNewBlock[],
  ): void => {
    setActiveContent({ blocks: mutate(activeContent.blocks) });
  };

  const updateBlock = (index: number, block: WhatsNewBlock): void =>
    setBlocks((blocks) => blocks.map((b, i) => (i === index ? block : b)));

  const addBlock = (block: WhatsNewBlock): void =>
    setBlocks((blocks) => [...blocks, block]);

  const removeBlock = (index: number): void =>
    setBlocks((blocks) => blocks.filter((_, i) => i !== index));

  const moveBlock = (index: number, dir: -1 | 1): void =>
    setBlocks((blocks) => {
      const target = index + dir;
      if (target < 0 || target >= blocks.length) return blocks;
      const next = [...blocks];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  const copyFromEnglish = (): void => {
    if (activeLang === 'en') return;
    setActiveContent({ title: form.title, blocks: [...form.blocks] });
  };

  const translationIsEmpty = (locale: WhatsNewLocale): boolean => {
    const tr = form.translations[locale];
    return !tr || (!tr.title.trim() && tr.blocks.length === 0);
  };

  // ─── Image upload ────────────────────────────────────────

  const pickImage = (targetIndex: number | null): void => {
    uploadTargetRef.current = targetIndex;
    fileInputRef.current?.click();
  };

  const handleFile = async (files: FileList | null): Promise<void> => {
    if (!files || files.length === 0) return;
    setSaveError(null);
    try {
      const formData = new FormData();
      formData.append('file', files[0]);
      const { url } = await uploadImage(formData).unwrap();
      const target = uploadTargetRef.current;
      if (target === null) {
        addBlock({ type: 'image', url });
      } else {
        updateBlock(target, { type: 'image', url });
      }
    } catch (err) {
      setSaveError(extractErrorMessage(err));
    } finally {
      uploadTargetRef.current = null;
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ─── Save / delete ───────────────────────────────────────

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSaveError(null);
    if (form.isPublished && !form.iosVersion && !form.androidVersion) {
      setSaveError(
        'Set an iOS and/or Android version — a published entry with no target version will never be shown.',
      );
      return;
    }
    // Drop empty translations so the app cleanly falls back to English.
    const translations: WhatsNewTranslations = {};
    TRANSLATED_LOCALES.forEach((locale) => {
      if (!translationIsEmpty(locale)) {
        translations[locale] = form.translations[locale];
      }
    });
    const payload = {
      title: form.title,
      blocks: form.blocks,
      translations: Object.keys(translations).length ? translations : null,
      iosVersion: form.iosVersion,
      androidVersion: form.androidVersion,
      isPublished: form.isPublished,
    };
    try {
      if (editingId === 'new') {
        await createEntry(payload).unwrap();
      } else if (editingId) {
        await updateEntry({ id: editingId, ...payload }).unwrap();
      }
      closeEditor();
    } catch (err) {
      setSaveError(extractErrorMessage(err));
    }
  };

  const onDelete = async (id: string): Promise<void> => {
    if (!window.confirm('Delete this entry? This cannot be undone.')) return;
    try {
      await deleteEntry(id).unwrap();
      if (editingId === id) closeEditor();
    } catch {
      window.alert('Failed to delete entry. Try again.');
    }
  };

  if (isLoading && !data) {
    return <div className="admin-users__loading">Loading What&apos;s New…</div>;
  }

  if (error) {
    return (
      <div className="admin-users__error">
        Failed to load What&apos;s New entries. Try again.
      </div>
    );
  }

  // ─── Editor ──────────────────────────────────────────────

  if (editingId !== null) {
    return (
      <form className="app-settings" onSubmit={onSubmit}>
        <div className="admin-users__header">
          <div className="admin-users__title">
            <h2>{editingId === 'new' ? 'New entry' : 'Edit entry'}</h2>
          </div>
          <div className="app-settings__save-row">
            <button
              type="button"
              className="admin-users__page-btn"
              onClick={closeEditor}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary app-settings__save-btn"
              disabled={isSaving}
            >
              {isSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        {saveError && <div className="admin-users__error">{saveError}</div>}

        <section className="user-detail__card user-detail__card--wide">
          <h3 className="user-detail__card-title">Release</h3>
          <div className="app-settings__grid">
            <label className="app-settings__field">
              <span className="app-settings__field-label">iOS version</span>
              <input
                type="text"
                className="app-settings__input"
                placeholder="e.g. 1.4.0 (empty = not shown on iOS)"
                value={form.iosVersion}
                onChange={(e) =>
                  setForm({ ...form, iosVersion: e.target.value })
                }
              />
            </label>
            <label className="app-settings__field">
              <span className="app-settings__field-label">
                Android version
              </span>
              <input
                type="text"
                className="app-settings__input"
                placeholder="e.g. 1.4.0 (empty = not shown on Android)"
                value={form.androidVersion}
                onChange={(e) =>
                  setForm({ ...form, androidVersion: e.target.value })
                }
              />
            </label>
          </div>
          <p className="app-settings__hint">
            The popup is shown once to users whose installed app version{' '}
            <strong>exactly matches</strong> the version above for their
            platform.
          </p>
          <label className="app-settings__toggle">
            <input
              type="checkbox"
              checked={form.isPublished}
              onChange={(e) =>
                setForm({ ...form, isPublished: e.target.checked })
              }
            />
            <span className="app-settings__toggle-track">
              <span className="app-settings__toggle-knob" />
            </span>
            <span className="app-settings__toggle-text">Published</span>
          </label>
        </section>

        <section className="user-detail__card user-detail__card--wide">
          <h3 className="user-detail__card-title">Content</h3>

          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {LOCALES.map(({ code, label }) => (
              <button
                key={code}
                type="button"
                className={
                  activeLang === code
                    ? 'btn btn-primary'
                    : 'admin-users__page-btn'
                }
                onClick={() => setActiveLang(code)}
              >
                {label}
                {code !== 'en' && translationIsEmpty(code) && ' ·'}
              </button>
            ))}
          </div>

          {activeLang !== 'en' && (
            <p className="app-settings__hint">
              Optional — users with this language see it instead of English.
              Left empty, they fall back to English.{' '}
              <button
                type="button"
                className="admin-users__page-btn"
                onClick={copyFromEnglish}
              >
                Copy from English
              </button>
            </p>
          )}

          <label className="app-settings__field">
            <span className="app-settings__field-label">Title</span>
            <input
              type="text"
              className="app-settings__input"
              placeholder="e.g. Clubs, faster chat and more"
              value={activeContent.title}
              onChange={(e) => setActiveContent({ title: e.target.value })}
            />
          </label>

          {activeContent.blocks.length === 0 && (
            <p className="app-settings__hint">
              No content yet — add headings, paragraphs and images below. They
              are shown in this order in the mobile popup.
            </p>
          )}

          {activeContent.blocks.map((block, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'flex-start',
                marginBottom: 12,
              }}
            >
              <div style={{ flex: 1 }}>
                <span className="app-settings__field-label">
                  {block.type === 'heading'
                    ? 'Heading'
                    : block.type === 'paragraph'
                      ? 'Paragraph'
                      : 'Image'}
                </span>
                {block.type === 'heading' && (
                  <input
                    type="text"
                    className="app-settings__input"
                    value={block.text}
                    placeholder="Section heading"
                    onChange={(e) =>
                      updateBlock(i, { type: 'heading', text: e.target.value })
                    }
                  />
                )}
                {block.type === 'paragraph' && (
                  <textarea
                    className="app-settings__textarea"
                    rows={3}
                    value={block.text}
                    placeholder="Describe the change…"
                    onChange={(e) =>
                      updateBlock(i, {
                        type: 'paragraph',
                        text: e.target.value,
                      })
                    }
                  />
                )}
                {block.type === 'image' && (
                  <div>
                    {block.url ? (
                      <img
                        src={block.url}
                        alt=""
                        style={{
                          maxWidth: 320,
                          maxHeight: 180,
                          borderRadius: 8,
                          display: 'block',
                          marginBottom: 6,
                        }}
                      />
                    ) : (
                      <p className="app-settings__hint">No image selected.</p>
                    )}
                    <button
                      type="button"
                      className="admin-users__page-btn"
                      onClick={() => pickImage(i)}
                      disabled={isUploading}
                    >
                      {isUploading ? 'Uploading…' : 'Replace image'}
                    </button>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 4, marginTop: 22 }}>
                <button
                  type="button"
                  className="admin-users__page-btn"
                  title="Move up"
                  disabled={i === 0}
                  onClick={() => moveBlock(i, -1)}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="admin-users__page-btn"
                  title="Move down"
                  disabled={i === activeContent.blocks.length - 1}
                  onClick={() => moveBlock(i, 1)}
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="admin-users__page-btn"
                  title="Remove block"
                  onClick={() => removeBlock(i)}
                >
                  ✕
                </button>
              </div>
            </div>
          ))}

          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="admin-users__page-btn"
              onClick={() => addBlock({ type: 'heading', text: '' })}
            >
              + Heading
            </button>
            <button
              type="button"
              className="admin-users__page-btn"
              onClick={() => addBlock({ type: 'paragraph', text: '' })}
            >
              + Paragraph
            </button>
            <button
              type="button"
              className="admin-users__page-btn"
              onClick={() => pickImage(null)}
              disabled={isUploading}
            >
              {isUploading ? 'Uploading…' : '+ Image'}
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            style={{ display: 'none' }}
            onChange={(e) => void handleFile(e.target.files)}
          />
        </section>
      </form>
    );
  }

  // ─── List ────────────────────────────────────────────────

  const hasItems = data && data.length > 0;

  return (
    <div className="admin-users">
      <div className="admin-users__header">
        <div className="admin-users__title">
          <h2>What&apos;s New</h2>
          {data && (
            <span className="admin-users__count">{data.length} total</span>
          )}
        </div>
        <button
          type="button"
          className="admin-users__page-btn"
          onClick={openNew}
        >
          + New entry
        </button>
      </div>

      {!hasItems && (
        <div className="admin-tab-empty">
          <h2 className="admin-tab-empty__title">No entries</h2>
          <p>
            Create an entry to show a &quot;What&apos;s New&quot; popup in the
            mobile app after an update.
          </p>
        </div>
      )}

      {hasItems && (
        <table className="admin-users__table">
          <thead>
            <tr>
              <th>Title</th>
              <th>iOS</th>
              <th>Android</th>
              <th>Languages</th>
              <th>Status</th>
              <th>Created</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.map((entry) => (
              <tr
                key={entry.id}
                onClick={() => openEntry(entry)}
                style={{ cursor: 'pointer' }}
              >
                <td>{entry.title || '(untitled)'}</td>
                <td>{entry.iosVersion || '—'}</td>
                <td>{entry.androidVersion || '—'}</td>
                <td>
                  {['en', ...Object.keys(entry.translations ?? {})]
                    .join(', ')
                    .toUpperCase()}
                </td>
                <td>{entry.isPublished ? 'Published' : 'Draft'}</td>
                <td>{new Date(entry.createdAt).toLocaleDateString()}</td>
                <td>
                  <button
                    type="button"
                    className="admin-users__page-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      void onDelete(entry.id);
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};
